import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchPricingSettings, updatePricingSettings } from '@/lib/businessValueApi';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

export default function LlmPricingSettingsPanel({ onSaved }) {
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('business-value', 'update');
  const [inputPrice, setInputPrice] = useState('0.003');
  const [outputPrice, setOutputPrice] = useState('0.015');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPricingSettings()
      .then((data) => {
        setInputPrice(String(data.llm_input_price_per_1k));
        setOutputPrice(String(data.llm_output_price_per_1k));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!canUpdate) return;
    setSaving(true);
    try {
      await updatePricingSettings({
        llm_input_price_per_1k: Number(inputPrice),
        llm_output_price_per_1k: Number(outputPrice),
      });
      toast.success('LLM pricing updated — dashboard totals will recalculate');
      onSaved?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message =
        err.response?.status === 404
          ? 'Pricing API is unavailable — restart or rebuild the dashboardapi service.'
          : detail || 'Failed to save LLM pricing';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="bv-pricing-settings">
      <CardHeader>
        <CardTitle>LLM token pricing</CardTitle>
        <CardDescription>
          Cost per 1,000 tokens for orchestrator Bedrock usage (generation + deployment events with tokens).
          Changes apply immediately to all dashboard calculations.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end gap-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Input price ($ / 1K tokens)
          </label>
          <Input
            type="number"
            min="0"
            step="0.0001"
            className="w-40"
            value={inputPrice}
            disabled={loading || !canUpdate}
            onChange={(e) => setInputPrice(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Output price ($ / 1K tokens)
          </label>
          <Input
            type="number"
            min="0"
            step="0.0001"
            className="w-40"
            value={outputPrice}
            disabled={loading || !canUpdate}
            onChange={(e) => setOutputPrice(e.target.value)}
          />
        </div>
        <span title={permissionDeniedTitle(canUpdate, 'save LLM pricing')}>
          <Button
            variant="outline"
            disabled={loading || saving || !canUpdate}
            onClick={handleSave}
          >
            <Save className="mr-1 h-4 w-4" />
            Save pricing
          </Button>
        </span>
      </CardContent>
    </Card>
  );
}
